import yaml from "js-yaml";

export const SPEC_EXTENSIONS = [".json", ".yaml", ".yml"] as const;

export function isSpecFile(fileName: string): boolean {
  const lower = fileName.toLowerCase();
  return SPEC_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

export function getApiNameFromFileName(fileName: string): string {
  const lower = fileName.toLowerCase();
  for (const ext of SPEC_EXTENSIONS) {
    if (lower.endsWith(ext)) {
      return fileName.slice(0, -ext.length);
    }
  }
  return fileName;
}

function isDirectChildOfSelectedFolder(relativePath: string): boolean {
  return relativePath.split("/").length === 2;
}

export function filterSpecFiles(
  files: File[],
  includeSubdirs: boolean,
): File[] {
  return files
    .filter((file) => {
      if (!isSpecFile(file.name)) return false;
      const relativePath = file.webkitRelativePath || file.name;
      if (!includeSubdirs && !isDirectChildOfSelectedFolder(relativePath)) {
        return false;
      }
      return true;
    })
    .sort((a, b) =>
      (a.webkitRelativePath || a.name).localeCompare(
        b.webkitRelativePath || b.name,
      ),
    );
}

function getFileExtension(fileName: string): string {
  const lower = fileName.toLowerCase();
  for (const ext of SPEC_EXTENSIONS) {
    if (lower.endsWith(ext)) return ext;
  }
  return "";
}

export async function readSpecContent(file: File): Promise<string> {
  const text = await file.text();
  const ext = getFileExtension(file.name);
  const label = file.webkitRelativePath || file.name;

  if (ext === ".json") {
    try {
      JSON.parse(text);
    } catch {
      throw new Error(`Invalid JSON in ${label}`);
    }
    return text;
  }

  if (ext === ".yaml" || ext === ".yml") {
    try {
      const parsed = yaml.load(text);
      return JSON.stringify(parsed);
    } catch {
      throw new Error(`Invalid YAML in ${label}`);
    }
  }

  throw new Error(`Unsupported file type: ${label}`);
}

export function getApiSpecDownloadFileName(
  apiName: string,
  version: string,
): string {
  const safeName =
    apiName.replace(/[^\w.-]+/g, "-").replace(/^-+|-+$/g, "") || "api-spec";
  const safeVersion =
    version.replace(/[^\w.-]+/g, "-").replace(/^-+|-+$/g, "") || "unknown";
  return `${safeName}-${safeVersion}.openapi.json`;
}

export function downloadApiSpec(specContent: string, fileName: string): void {
  const blob = new Blob([specContent], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}
